// Artwork specs + a tiny helper that checks an image file's proportions in the browser, so
// uploaders get an immediate warning if a poster or backdrop will be cropped badly in the app.
//
//  - Poster    (portrait 2:3)  → catalogue rows & grids (Netflix-style tall cards)
//  - Thumbnail (landscape 16:9) → hero banners, the title page, "Continue Watching", hover preview

export const POSTER_SPEC = {
  name: 'Poster',
  ratio: 2 / 3,
  shape: 'portrait 2:3',
  example: '1000 × 1500',
};

export const THUMBNAIL_SPEC = {
  name: 'Thumbnail',
  ratio: 16 / 9,
  shape: 'landscape 16:9',
  example: '1920 × 1080',
};

function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      reject(new Error('Could not read that image.'));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/**
 * Resolves `{ ok, width, height, message }`. `ok` is false when the image's proportions are more
 * than `tolerance` (default 8%) away from the spec's ratio. An unreadable image resolves with a
 * warning but is still allowed to upload; the server's own validation has the final say.
 */
export async function checkImageRatio(file, spec, tolerance = 0.08) {
  try {
    const { width, height } = await readImageSize(file);
    const actual = width / height;
    const off = Math.abs(actual - spec.ratio) / spec.ratio;
    if (off <= tolerance) return { ok: true, width, height, message: null };
    return {
      ok: false,
      width,
      height,
      message: `${spec.name} is ${width}×${height}. For best results use a ${spec.shape} image (e.g. ${spec.example}) — otherwise it will be cropped.`,
    };
  } catch {
    return {
      ok: false,
      width: 0,
      height: 0,
      message: `Could not check the ${spec.name.toLowerCase()} dimensions. You can still upload it, but make sure it is ${spec.shape}.`,
    };
  }
}
